/**
 * Atlas — Book for Client (Phase 1)
 *
 * `/atlas/clients/:client_id/book`
 *
 * Advisor-side booking form.  Awards a hardcoded 3× rewards
 * multiplier to the linked client's ledger regardless of the
 * override's `rewards_multiplier` (which only applies to future
 * self-bookings).  Two property modes:
 *
 *   1. Existing override — pick from the advisor's active
 *      `/api/admin/booking-overrides?active_only=true` list.
 *   2. New property — inline `display_name` / `city` / affiliate URL
 *      / provider / multiplier form.  When "make public TravelBio
 *      card" is checked (default), the backend upserts a
 *      `property_booking_overrides` row so future travelers see this
 *      property in Explore.
 *
 * CARDINAL RULE: this is an Atlas-only surface.  No component here
 * is shared with the Explore / traveler side.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Check,
  ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const ADVISOR_MULTIPLIER = 3; // hardcoded — see decision #8

const PROVIDER_OPTIONS = [
  { value: "booking_cj",      label: "Booking.com (via CJ)" },
  { value: "direct",          label: "Direct with hotel"    },
  { value: "other_affiliate", label: "Other affiliate"      },
  { value: "other",           label: "Other"                },
];

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────
export default function AtlasBookForClientPage() {
  const { client_id } = useParams();
  const nav = useNavigate();

  const [client, setClient]           = useState(null);
  const [overrides, setOverrides]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [busy, setBusy]               = useState(false);
  const [result, setResult]           = useState(null);

  // Form state
  const [bookingType, setBookingType]         = useState("accommodation"); // accommodation | car_rental | flight
  const [mode, setMode]                       = useState("existing"); // "existing" | "new"
  const [selectedKey, setSelectedKey]         = useState("");
  const [overrideQ, setOverrideQ]             = useState("");
  const [newDisplayName, setNewDisplayName]   = useState("");
  const [newCity, setNewCity]                 = useState("");
  const [newUrl, setNewUrl]                   = useState("");
  const [newProvider, setNewProvider]         = useState(PROVIDER_OPTIONS[0].value);
  const [newMult, setNewMult]                 = useState(2);
  const [makePublicCard, setMakePublicCard]   = useState(true);

  const [startDate, setStartDate]     = useState("");
  const [endDate, setEndDate]         = useState("");
  const [amount, setAmount]           = useState("");
  const [bookingRef, setBookingRef]   = useState("");
  const [notes, setNotes]             = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ov] = await Promise.all([
        api.get(`/atlas/clients/${client_id}`),
        api.get(`/admin/booking-overrides?active_only=true&limit=500`),
      ]);
      setClient(c.data.client);
      setOverrides(ov.data.overrides || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load client.");
      nav("/atlas/clients");
    } finally {
      setLoading(false);
    }
  }, [client_id, nav]);

  useEffect(() => { load(); }, [load]);

  const filteredOverrides = useMemo(() => {
    const q = overrideQ.trim().toLowerCase();
    if (!q) return overrides;
    return overrides.filter((o) =>
      (o.display_name || "").toLowerCase().includes(q)
      || (o.property_key || "").toLowerCase().includes(q));
  }, [overrides, overrideQ]);

  const amtNum = parseFloat(amount) || 0;
  const isGeneric = bookingType !== "accommodation";
  const activeMultiplier = isGeneric ? 1 : ADVISOR_MULTIPLIER;
  const previewPts = Math.floor(amtNum / 10) * activeMultiplier;

  const canSubmit = (() => {
    if (!startDate || !endDate) return false;
    if (endDate < startDate) return false;
    if (!amount || amtNum <= 0) return false;
    if (!bookingRef.trim()) return false;
    if (isGeneric) return true; // no property picker for car/flight
    if (mode === "existing" && !selectedKey) return false;
    if (mode === "new") {
      if (!newDisplayName.trim()) return false;
      if (!newProvider) return false;
      if (makePublicCard && !newUrl.trim()) return false;
    }
    return true;
  })();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setResult(null);
    try {
      const body = {
        trip: {
          start_date:          startDate,
          end_date:            endDate,
          booking_amount_usd:  amtNum,
          booking_reference:   bookingRef.trim(),
          notes:               notes.trim() || null,
        },
      };
      if (isGeneric) {
        body.booking_type = bookingType;
      } else if (mode === "existing") {
        body.property_key = selectedKey;
      } else {
        body.new_override = {
          display_name:       newDisplayName.trim(),
          city:               newCity.trim(),
          custom_booking_url: newUrl.trim(),
          provider:           newProvider,
          rewards_multiplier: parseInt(newMult, 10) || 2,
          make_public_card:   makePublicCard,
        };
      }
      const { data } = await api.post(
        `/atlas/clients/${client_id}/book`, body);
      setResult(data);
      const msg = data.idempotent
        ? `Already booked (idempotent replay) — ${data.points_awarded_lifetime} pts`
        : `Booked · +${data.points_awarded_lifetime} pts to ${client?.display_name}`;
      toast.success(msg, { duration: 8000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed.",
                    { duration: 10000 });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 font-hand text-tb-navy/50 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!client) return null;

  const notLinked = !client.linked_user_id;

  return (
    <div className="min-h-screen bg-white">
      <div className="p-6 md:p-10 max-w-3xl mx-auto">
        <Link to={`/atlas/clients/${client_id}`}
              data-testid="atlas-book-back"
              className="inline-flex items-center gap-1 font-hand text-xs text-tb-navy/60 hover:text-tb-navy">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {client.display_name}
        </Link>

        <h1 className="mt-4 text-tb-navy text-3xl md:text-4xl font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
            data-testid="atlas-book-title">
          Book for {client.display_name}
        </h1>
        <p className="mt-1 font-hand text-tb-navy/60 text-sm">
          Advisor bookings earn <strong>3×</strong> rewards for the client
          regardless of the property's default multiplier.
        </p>

        {notLinked && (
          <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4"
               data-testid="atlas-book-not-linked">
            <p className="font-hand text-sm text-amber-900">
              This client is not linked to a TravelBio account yet.
              You can only book for linked clients so the points land
              on a real ledger.
            </p>
            <Link to={`/atlas/clients/${client_id}`}
                  className="inline-block mt-2 h-8 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs">
              Back &amp; link a user
            </Link>
          </div>
        )}

        {!notLinked && (
          <form onSubmit={submit} className="mt-6 space-y-6"
                data-testid="atlas-book-form">

            {/* BOOKING TYPE SECTION */}
            <section className="rounded-2xl border border-tb-navy/15 bg-white p-5">
              <h2 className="text-tb-navy font-semibold text-lg mb-3"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                Booking type
              </h2>
              <div className="grid grid-cols-3 gap-2" role="radiogroup">
                {[
                  { v: "accommodation", label: "Accommodation", mult: "3×" },
                  { v: "car_rental",    label: "Car rental",    mult: "1×" },
                  { v: "flight",        label: "Flight",        mult: "1×" },
                ].map((opt) => (
                  <button key={opt.v} type="button"
                    role="radio"
                    aria-checked={bookingType === opt.v}
                    onClick={() => setBookingType(opt.v)}
                    data-testid={`atlas-book-type-${opt.v}`}
                    className={"h-16 rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 font-hand text-sm transition-colors "
                      + (bookingType === opt.v
                          ? "border-tb-navy bg-tb-navy text-tb-gold-bright"
                          : "border-tb-navy/20 bg-white text-tb-navy hover:border-tb-navy/40")}>
                    <span>{opt.label}</span>
                    <span className={"text-[10px] " + (bookingType === opt.v ? "text-tb-gold-bright/80" : "text-tb-navy/50")}>
                      earns {opt.mult}
                    </span>
                  </button>
                ))}
              </div>
              {isGeneric && (
                <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3"
                     data-testid="atlas-book-generic-banner">
                  <p className="font-hand text-xs text-amber-900">
                    Client will earn <strong>1× points</strong> on {bookingType === "car_rental" ? "car rentals" : "flights"},
                    regardless of who books. The booking flows through the
                    global {bookingType === "car_rental" ? "car rental" : "flight"} affiliate link
                    configured in <code>/admin/booking-overrides</code>.
                  </p>
                </div>
              )}
            </section>

            {/* PROPERTY SECTION — only for accommodation */}
            {!isGeneric && (
            <section className="rounded-2xl border border-tb-navy/15 bg-white p-5">
              <h2 className="text-tb-navy font-semibold text-lg mb-3"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                Property
              </h2>
              <div className="flex gap-2 mb-4" role="tablist">
                <button type="button"
                  onClick={() => setMode("existing")}
                  data-testid="atlas-book-mode-existing"
                  className={"h-9 px-4 rounded-full font-hand text-sm border "
                    + (mode === "existing"
                        ? "bg-tb-navy text-tb-gold-bright border-tb-navy"
                        : "bg-white text-tb-navy border-tb-navy/25")}>
                  Existing property
                </button>
                <button type="button"
                  onClick={() => setMode("new")}
                  data-testid="atlas-book-mode-new"
                  className={"h-9 px-4 rounded-full font-hand text-sm border "
                    + (mode === "new"
                        ? "bg-tb-navy text-tb-gold-bright border-tb-navy"
                        : "bg-white text-tb-navy border-tb-navy/25")}>
                  New property
                </button>
              </div>

              {mode === "existing" ? (
                <div>
                  <input type="text" value={overrideQ}
                    onChange={(e) => setOverrideQ(e.target.value)}
                    placeholder="Search overrides…"
                    data-testid="atlas-book-override-search"
                    className="w-full h-10 px-3 mb-2 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                  <div className="max-h-64 overflow-auto rounded-lg border border-tb-navy/15">
                    {filteredOverrides.length === 0 ? (
                      <div className="p-3 font-hand text-xs text-tb-navy/50">
                        No active overrides match.
                      </div>
                    ) : filteredOverrides.map((o) => (
                      <button key={o.property_key} type="button"
                        onClick={() => setSelectedKey(o.property_key)}
                        data-testid={`atlas-book-override-row-${o.property_key}`}
                        className={"w-full text-left px-3 py-2 border-b border-tb-navy/8 hover:bg-tb-navy/5 flex items-start justify-between gap-2 "
                          + (selectedKey === o.property_key ? "bg-tb-navy/5" : "")}>
                        <div className="min-w-0 flex-1">
                          <div className="font-hand text-sm text-tb-navy truncate">
                            {o.display_name}
                          </div>
                          <div className="font-hand text-[11px] text-tb-navy/60 truncate">
                            {o.property_key} · {o.provider} · {o.rewards_multiplier}×
                          </div>
                        </div>
                        {selectedKey === o.property_key && (
                          <Check className="w-4 h-4 text-tb-navy shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid gap-3">
                  <div>
                    <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Property name *</label>
                    <input value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      data-testid="atlas-book-new-name"
                      placeholder="Saratoga Casino Hotel"
                      className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                  </div>
                  <div>
                    <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">City</label>
                    <input value={newCity}
                      onChange={(e) => setNewCity(e.target.value)}
                      data-testid="atlas-book-new-city"
                      placeholder="Saratoga Springs"
                      className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                  </div>
                  <div>
                    <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Affiliate URL *</label>
                    <input value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      data-testid="atlas-book-new-url"
                      placeholder="https://www.dpbolvw.net/click-xxx-xxxxxx"
                      className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                    <p className="mt-1 font-hand text-[11px] text-tb-navy/50">
                      This URL will also become the property's public booking link if the checkbox below is on.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Provider</label>
                      <select value={newProvider}
                        onChange={(e) => setNewProvider(e.target.value)}
                        data-testid="atlas-book-new-provider"
                        className="w-full h-10 px-2 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy">
                        {PROVIDER_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Public multiplier</label>
                      <input type="number" min="1" max="10"
                        value={newMult}
                        onChange={(e) => setNewMult(e.target.value)}
                        data-testid="atlas-book-new-mult"
                        className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                    </div>
                  </div>
                  <label className="mt-1 inline-flex items-start gap-2 cursor-pointer"
                         data-testid="atlas-book-new-public-label">
                    <input type="checkbox"
                      checked={makePublicCard}
                      onChange={(e) => setMakePublicCard(e.target.checked)}
                      data-testid="atlas-book-new-public"
                      className="mt-1" />
                    <span className="font-hand text-xs text-tb-navy/80">
                      Also make this a public TravelBio card for future travelers.
                      Their self-bookings will earn the public multiplier ({newMult}×);
                      this advisor booking always earns 3×.
                    </span>
                  </label>
                </div>
              )}
            </section>
            )}

            {/* TRIP SECTION */}
            <section className="rounded-2xl border border-tb-navy/15 bg-white p-5">
              <h2 className="text-tb-navy font-semibold text-lg mb-3"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                Trip details
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Check-in *</label>
                  <input type="date" value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="atlas-book-start"
                    className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                </div>
                <div>
                  <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Check-out *</label>
                  <input type="date" value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="atlas-book-end"
                    className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Amount (USD) *</label>
                  <input type="number" step="0.01" min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="atlas-book-amount"
                    placeholder="425.00"
                    className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                </div>
                <div>
                  <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Booking reference *</label>
                  <input value={bookingRef}
                    onChange={(e) => setBookingRef(e.target.value)}
                    data-testid="atlas-book-ref"
                    placeholder="CJ-2026-08-12345 or manual tag"
                    className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
                </div>
              </div>
              <div className="mt-3">
                <label className="block font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-1">Notes</label>
                <textarea value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="atlas-book-notes"
                  rows={2}
                  className="w-full rounded-lg border border-tb-navy/20 p-2 font-hand text-sm text-tb-navy" />
              </div>
              <div className="mt-4 rounded-lg bg-tb-navy/5 p-3 flex items-center justify-between"
                   data-testid="atlas-book-preview">
                <span className="font-hand text-xs text-tb-navy/70">
                  Points to award ({activeMultiplier}× {isGeneric ? bookingType.replace("_", " ") : "advisor"} rate)
                </span>
                <span className="font-hand text-lg font-semibold text-tb-navy tabular-nums">
                  +{previewPts} pts
                </span>
              </div>
            </section>

            <button type="submit"
              disabled={busy || !canSubmit}
              data-testid="atlas-book-submit"
              className="w-full h-12 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-base inline-flex items-center justify-center gap-2 disabled:opacity-40">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirm booking
            </button>

            {result && (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4"
                   data-testid="atlas-book-success">
                <div className="font-hand text-sm text-emerald-900">
                  {result.idempotent
                    ? "Already recorded — no double-award."
                    : "Booked."}
                </div>
                <ul className="mt-2 font-hand text-xs text-emerald-900/90 space-y-0.5">
                  <li>Property: <strong>{result.override_property_key}</strong>
                    {result.override_created && <> · <em>override created</em></>}
                  </li>
                  <li>+{result.points_awarded_lifetime} lifetime pts,
                      +{result.points_awarded_redeemable} redeemable pts</li>
                  <li>Chapter created: {result.chapter_created ? "yes" : "no (future date)"}</li>
                  <li>Client notified: {result.notified ? "yes" : "no"}</li>
                </ul>
                <div className="mt-3 flex gap-2">
                  <Link to={`/atlas/clients/${client_id}`}
                    className="h-8 px-3 rounded-full border border-emerald-400 bg-white text-emerald-900 font-hand text-xs inline-flex items-center gap-1">
                    Back to client
                  </Link>
                  <a href={`/trips/${result.booked_trip_id}`}
                    target="_blank" rel="noreferrer"
                    className="h-8 px-3 rounded-full bg-emerald-700 text-white font-hand text-xs inline-flex items-center gap-1">
                    View client trip <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
